'use client';

import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Square } from 'lucide-react';

// 听写模式组件
interface DictationModeProps {
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  audioLevel: number;
  barsConfig: Array<{ duration: number; delay: number }>;
  onStop: () => void;
}

const DictationMode: React.FC<DictationModeProps> = ({
  isListening,
  transcript,
  interimTranscript,
  audioLevel,
  barsConfig,
  onStop,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const waveOffsetRef = useRef(0);
  const waveHistoryRef = useRef<number[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 设置 Canvas 尺寸
    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // 动画循环
    const animate = () => {
      const width = canvas.width / window.devicePixelRatio;
      const height = canvas.height / window.devicePixelRatio;

      // 清空画布
      ctx.clearRect(0, 0, width, height);

      // 更新波形偏移（从右往左移动）
      waveOffsetRef.current += 2; // 控制移动速度

      // 如果有声音，添加新的波形数据
      if (audioLevel > 0.01) {
        waveHistoryRef.current.push(audioLevel);
        // 限制历史数据长度
        if (waveHistoryRef.current.length > 200) {
          waveHistoryRef.current.shift();
        }
      }

      // 绘制虚线和波形
      const dashLength = 8;
      const dashGap = 4;
      const totalDash = dashLength + dashGap;

      ctx.save();

      for (let x = 0; x < width; x += 2) {
        // 计算当前位置对应的虚线段
        const dashPosition = (x + waveOffsetRef.current) % totalDash;

        // 判断是否在虚线段内
        if (dashPosition < dashLength) {
          // 计算颜色渐变（从右到左，灰色到白色）
          const colorProgress = x / width;
          const grayValue = Math.floor(117 + (255 - 117) * (1 - colorProgress)); // 从#757575到#ffffff
          const opacity = 0.6 + 0.4 * (1 - colorProgress);

          ctx.strokeStyle = `rgba(${grayValue}, ${grayValue}, ${grayValue}, ${opacity})`;
          ctx.lineWidth = 1;

          // 计算波形高度
          let waveHeight = 0;
          const dataIndex = Math.floor((width - x) / 2);

          if (dataIndex < waveHistoryRef.current.length && audioLevel > 0.01) {
            const historicalLevel =
              waveHistoryRef.current[waveHistoryRef.current.length - 1 - dataIndex];
            if (historicalLevel) {
              // 创建波浪效果
              const time = Date.now() / 1000;
              const frequency = 0.05;
              const phase = (x / width) * Math.PI * 4;
              waveHeight = historicalLevel * 15 * Math.sin(time * 2 + phase);
            }
          }

          // 绘制虚线段
          ctx.beginPath();
          ctx.moveTo(x, height / 2 - waveHeight);
          ctx.lineTo(x + 2, height / 2 - waveHeight);
          ctx.stroke();

          // 如果有波形，绘制对称的下半部分
          if (waveHeight !== 0) {
            ctx.beginPath();
            ctx.moveTo(x, height / 2 + waveHeight);
            ctx.lineTo(x + 2, height / 2 + waveHeight);
            ctx.stroke();
          }
        }
      }

      ctx.restore();

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [audioLevel, isListening]);

  return (
    <div className="relative min-h-[70px]">
      {/* 状态文字 */}
      <div className="text-text-tertiary text-[14px] leading-[18px]">
        {isListening ? 'Listening...' : 'Initializing...'}
      </div>

      {/* 识别的文字 - 中间区域 */}
      <div className="text-white text-[14px] mt-2 mb-8 h-[42px] overflow-y-auto scrollbar-hide">
        {transcript}
        <span className="text-text-tertiary">{interimTranscript}</span>
      </div>

      {/* 底部波形区域 - Canvas 实现 */}
      <div className="absolute bottom-[18px] left-0 right-[44px] h-[20px]">
        <canvas ref={canvasRef} className="w-full h-full" style={{ display: 'block' }} />
      </div>

      {/* 彩色光晕背景 */}
      <div
        className="absolute bottom-[6px] right-[-4px] w-[44px] h-[44px] rounded-full pointer-events-none"
        style={{
          background:
            'conic-gradient(from 236.31deg at 50% 50%, #520E7C -10.38deg, #C91AE2 8.65deg, #3F62FF 90deg, #37FB45 159.23deg, #FFD900 252.69deg, #520E7C 349.62deg, #C91AE2 368.65deg)',
          filter: 'blur(8px)',
          opacity: 0.8,
        }}
      />

      {/* 结束按钮 - 白色背景 */}
      <button
        type="button"
        onClick={onStop}
        className={cn(
          'absolute bottom-[10px] right-0',
          'w-9 h-9',
          'flex items-center justify-center',
          'hover:opacity-90 transition-opacity duration-200',
          'group',
          'rounded-full',
          'bg-white',
          'shadow-lg',
        )}
        style={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)', // Safari support
        }}
        aria-label="结束听写"
      >
        <Square size={12} className="bg-black border-0 text-black rounded-[4px]" />
      </button>
    </div>
  );
};

export default DictationMode;
